import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ApiError, adminApi, type AdminDropbox } from '../lib/api'
import { Alert, Spinner } from './ui'

/**
 * Deleting a dropbox is the one action here with no undo: the objects leave
 * storage and the rows cascade away. So the confirmation asks for the dropbox's
 * short id to be typed rather than offering a button to click through.
 *
 * Eight characters is the deliberate middle: long enough that it cannot come
 * from muscle memory, short enough to retype without reaching for copy-paste —
 * a full uuid is so tedious that people paste it without reading, which defeats
 * the pause the confirmation exists to create. The server checks it too.
 */
export const shortId = (id: string) => id.slice(0, 8)

export function DangerZone({ token, dropbox }: { token: string; dropbox: AdminDropbox }) {
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')

  const destroy = useMutation({
    mutationFn: () => adminApi.destroy(token, typed.trim()),
  })

  if (destroy.isSuccess) {
    return (
      <div className="mt-4">
        <Alert tone="success" title="Dropbox deleted">
          {destroy.data.objectsRemoved} object(s) removed from storage
          {destroy.data.uploadsAborted > 0 &&
            `, ${destroy.data.uploadsAborted} unfinished upload(s) aborted`}
          . This link no longer works.
        </Alert>
      </div>
    )
  }

  const matches = typed.trim().toLowerCase() === shortId(dropbox.id)

  return (
    <div className="card mt-4 border-red-200 p-5 sm:p-6 dark:border-red-900/60">
      <h2 className="text-sm font-semibold text-red-700 dark:text-red-400">Danger zone</h2>

      {!open ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="min-w-0 flex-1 text-sm text-zinc-600 dark:text-zinc-400">
            Delete this dropbox, every uploaded file and every note. There is no undo.
          </p>
          <button type="button" onClick={() => setOpen(true)} className="btn-danger">
            Delete dropbox
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <Alert tone="error" title="This cannot be undone">
            Deleting removes the uploaded files from storage permanently, along with the notes, the
            activity log and both links. Download anything you still need first.
          </Alert>

          <div>
            <label className="label" htmlFor="confirm-id">
              Type <code className="font-mono font-bold">{shortId(dropbox.id)}</code> to confirm
            </label>
            <input
              id="confirm-id"
              className="input max-w-48 font-mono tracking-widest"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={shortId(dropbox.id).replace(/./g, '·')}
              maxLength={8}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {destroy.isError && (
            <Alert tone="error" title="Could not delete">
              {destroy.error instanceof ApiError ? destroy.error.message : 'Unexpected error.'}
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!matches || destroy.isPending}
              onClick={() => destroy.mutate()}
              className="btn-danger"
              title={matches ? undefined : 'Type the short id exactly'}
            >
              {destroy.isPending && <Spinner />}
              Permanently delete
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setTyped('')
                destroy.reset()
              }}
              disabled={destroy.isPending}
              className="btn-ghost"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
